import React from 'react';
import { Code2, Server, Database, Cloud, Brain } from 'lucide-react';
import { ScrollReveal, StaggerContainer, StaggerItem } from '@/components/ui/ScrollReveal';
import { Container, Section } from '@/components/layout/Container';
import { Badge } from '@/components/ui/Badge';
import { NeuralNetwork } from '@/components/animations/NeuralNetwork';
import { FloatingNodes } from '@/components/animations/FloatingNodes';

const techStack = {
  frontend: {
    icon: <Code2 className="w-4 h-4" />,
    label: 'Frontend',
    items: ['React', 'Next.js', 'Vue', 'Vite', 'TypeScript'],
    color: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  },
  backend: {
    icon: <Server className="w-4 h-4" />,
    label: 'Backend',
    items: ['Go', 'Node.js', 'Express', 'REST APIs', 'GraphQL'],
    color: 'bg-green-500/10 text-green-400 border-green-500/20',
  },
  database: {
    icon: <Database className="w-4 h-4" />,
    label: 'Database',
    items: ['PostgreSQL', 'SQLite', 'Prisma ORM', 'Redis'],
    color: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  },
  infrastructure: {
    icon: <Cloud className="w-4 h-4" />,
    label: 'Infrastructure',
    items: ['VPS', 'Docker', 'SSH', 'CI/CD', 'Monitoring'],
    color: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  },
  ai: {
    icon: <Brain className="w-4 h-4" />,
    label: 'AI Integration',
    items: ['OpenAI API', 'Prompt Engineering', 'Workflow Orchestration', 'RAG'],
    color: 'bg-apple-blue/10 text-apple-blue border-apple-blue/20',
  },
};

const integrationOutcomes = [
  'Clinical and engineering teams aligned on one decision model.',
  'Regulatory, security, and workflow constraints surfaced early.',
  'Faster implementation cycles with reduced rework risk.',
  'Executive-ready roadmap and governance checkpoints.',
];

const deliveryFlow = [
  {
    step: '1. Diagnose',
    description: 'Map constraints, stakeholders, and failure points in current workflows.',
  },
  {
    step: '2. Design',
    description: 'Define integration architecture, governance gates, and rollout strategy.',
  },
  {
    step: '3. Deploy',
    description: 'Support implementation, measurement, and executive decision cadence.',
  },
];

export const TechStack: React.FC = () => {
  return (
    <Section id="expertise" dark>
      <Container>
        {/* Section Header */}
        <ScrollReveal className="text-center mb-16">
          <span className="text-caption text-apple-blue uppercase tracking-wider font-medium">
            Technical Capabilities
          </span>
          <h2 className="text-h1 md:text-display-m font-bold mt-4 mb-6">
            Health-<span className="gradient-text">Tech</span> Integration
          </h2>
          <p className="text-body-l text-text-secondary max-w-2xl mx-auto">
            Unlike traditional medical consultants, I operate hands-on in technology. 
            I understand both clinical constraints and engineering constraints.
          </p>
        </ScrollReveal>

        <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-8 xl:gap-12 items-start">
          <ScrollReveal direction="left" className="lg:sticky lg:top-24">
            <div className="relative min-w-0 space-y-4">
              <div className="relative bg-bg-tertiary rounded-3xl border border-border-subtle p-6 md:p-7 overflow-hidden">
                <FloatingNodes count={8} />
                <div className="sm:hidden">
                  <NeuralNetwork width={250} height={190} className="mx-auto max-w-full" />
                </div>
                <div className="hidden sm:block">
                  <NeuralNetwork width={350} height={280} className="mx-auto max-w-full" />
                </div>
                <div className="absolute top-4 left-4 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-apple-blue animate-pulse" />
                  <span className="text-caption text-text-tertiary">Live Processing</span>
                </div>

                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                  {integrationOutcomes.map((outcome) => (
                    <div
                      key={outcome}
                      className="rounded-xl border border-border-subtle bg-bg-primary/55 p-3"
                    >
                      <p className="text-body-s text-text-secondary leading-relaxed">{outcome}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-border-subtle bg-bg-tertiary/70 p-4">
                <p className="text-caption uppercase tracking-wider text-apple-blue mb-2">
                  Why It Works
                </p>
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {['Clinical Context', 'Technical Execution', 'Risk Governance', 'Fast Alignment'].map(
                    (pill) => (
                      <div
                        key={pill}
                        className="rounded-lg bg-bg-primary/50 border border-border-subtle px-3 py-2"
                      >
                        <span className="text-body-s text-text-secondary">{pill}</span>
                      </div>
                    ),
                  )}
                </div>
              </div>
            </div>
          </ScrollReveal>

          <div className="min-w-0 space-y-5">
            <ScrollReveal>
              <div className="rounded-3xl border border-border-subtle bg-bg-primary/40 p-5 md:p-6 lg:p-7">
                <h3 className="text-h3 font-semibold mb-3">Capability Matrix</h3>
                <p className="text-body text-text-secondary mb-5">
                  This is the practical stack I use to bridge product, engineering, and clinical
                  decision-making without adding coordination overhead.
                </p>

                <StaggerContainer className="grid md:grid-cols-2 gap-4 lg:gap-5">
                  {Object.values(techStack).map((category) => (
                    <StaggerItem key={category.label}>
                      <div className="h-full rounded-xl border border-border-subtle bg-bg-tertiary/70 p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <div className={`p-1.5 rounded-lg ${category.color}`}>{category.icon}</div>
                          <span className="font-medium text-text-primary">{category.label}</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {category.items.map((item) => (
                            <Badge key={item} variant="outline" className="text-xs leading-tight">
                              {item}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </StaggerItem>
                  ))}
                </StaggerContainer>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              <div className="rounded-2xl border border-border-subtle bg-bg-tertiary/70 p-5">
                <p className="text-caption uppercase tracking-wider text-apple-blue mb-3">
                  Delivery Pattern
                </p>
                <div className="grid md:grid-cols-3 gap-3">
                  {deliveryFlow.map((phase) => (
                    <div
                      key={phase.step}
                      className="rounded-xl border border-border-subtle bg-bg-primary/50 p-4"
                    >
                      <p className="text-body-s font-medium text-text-primary mb-2">{phase.step}</p>
                      <p className="text-body-s text-text-secondary">{phase.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </Container>
    </Section>
  );
};

export default TechStack;
